import { useEffect, useRef, useState } from "react";
import { Upload, Loader2, Trash2, Film, Star } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CHUNK = 8 * 1024 * 1024;

async function uploadBumper(file, kind, ownerId, onProgress) {
  const { data } = await api.post("/clip-bumpers/init", { kind, title: file.name.replace(/\.[^.]+$/, ""), size: file.size, owner_id: ownerId });
  for (let off = 0; off < file.size; off += CHUNK) {
    await api.put(`/clip-bumpers/${data.id}/chunk`, file.slice(off, off + CHUNK), { headers: { "Content-Type": "application/octet-stream" } });
    onProgress(Math.round((Math.min(off + CHUNK, file.size) / file.size) * 100));
  }
  await api.post(`/clip-bumpers/${data.id}/complete`);
}

function BumperSlot({ kind, items, value, onChange, ownerId, reload, defaultId }) {
  const input = useRef(null);
  const [pct, setPct] = useState(null);
  const ready = items.filter((b) => b.kind === kind && b.status === "ready");
  const pending = items.filter((b) => b.kind === kind && b.status === "processing").length;
  const failed = items.filter((b) => b.kind === kind && b.status === "failed");
  const label = kind === "intro" ? "Intro video" : "Outro video";

  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setPct(0);
    try { await uploadBumper(f, kind, ownerId, setPct); toast.success(`${label} uploaded. It'll be ready in a moment.`); reload(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); } finally { setPct(null); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.title}"? Clips already made with it are not affected.`)) return;
    try { await api.delete(`/clip-bumpers/${b.id}`); if (value === b.id) onChange(""); reload(); } catch { toast.error("Couldn't delete"); }
  };

  return (
    <div className="space-y-1.5 flex-1 min-w-0" data-testid={`clip-${kind}-slot`}>
      <Label className="text-gray-300 text-xs">{label}</Label>
      <div className="flex gap-2">
        <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
          <SelectTrigger data-testid={`clip-${kind}-select`} className="bg-gray-950 border-gray-800 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card border-gray-800">
            <SelectItem value="none">No {kind}</SelectItem>
            {ready.map((b) => <SelectItem key={b.id} value={b.id}>{b.title} · {Math.round(b.duration_seconds)}s{b.id === defaultId ? " · default" : ""}</SelectItem>)}
          </SelectContent>
        </Select>
        <input ref={input} type="file" accept="video/*" className="hidden" onChange={pick} data-testid={`clip-${kind}-file`} />
        <button data-testid={`clip-${kind}-upload`} type="button" disabled={pct !== null} onClick={() => input.current?.click()} title={`Upload a new ${kind}`}
          className="h-9 px-2.5 shrink-0 rounded-lg border border-gray-800 text-xs text-gray-300 hover:text-amber-400 hover:border-amber-500/40 inline-flex items-center gap-1 transition-colors disabled:opacity-60">
          {pct !== null ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> {pct}%</> : <><Upload className="h-3.5 w-3.5" /> Upload</>}
        </button>
        {value && (
          <button data-testid={`clip-${kind}-delete`} type="button" onClick={() => remove(ready.find((b) => b.id === value))} title="Delete this video"
            className="h-9 w-9 shrink-0 rounded-lg border border-gray-800 text-gray-500 hover:text-red-400 hover:border-red-500/40 flex items-center justify-center transition-colors">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {pending > 0 && <p className="text-[11px] text-amber-300 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Preparing {pending} upload{pending > 1 ? "s" : ""}…</p>}
      {failed.map((b) => <p key={b.id} className="text-[11px] text-red-400">"{b.title}" failed: {b.error || "unknown error"}</p>)}
    </div>
  );
}

export function ClipBumpers({ ownerId, intro, outro, setIntro, setOutro }) {
  const [items, setItems] = useState([]);
  const [defaults, setDefaults] = useState({ intro_id: null, outro_id: null });
  const [savingDef, setSavingDef] = useState(false);
  const load = async () => {
    try { const r = await api.get("/clip-bumpers", { params: { owner_id: ownerId } }); setItems(r.data); } catch { /* shown as empty */ }
  };
  const loadDefaults = async () => {
    try {
      const r = await api.get("/clip-defaults", { params: { owner_id: ownerId } });
      setDefaults(r.data);
      if (r.data.intro_id) setIntro(r.data.intro_id);
      if (r.data.outro_id) setOutro(r.data.outro_id);
    } catch { /* no defaults */ }
  };
  const saveDefaults = async () => {
    setSavingDef(true);
    try {
      const r = await api.put("/clip-defaults", { intro_id: intro || null, outro_id: outro || null, owner_id: ownerId });
      setDefaults(r.data); toast.success("Saved. New clips will start with this intro and outro.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save defaults"); } finally { setSavingDef(false); }
  };
  useEffect(() => { if (ownerId) { load(); loadDefaults(); } /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ownerId]);
  useEffect(() => {
    if (!items.some((b) => ["processing", "uploading"].includes(b.status))) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  return (
    <div className="space-y-2">
      <p className="text-sm font-semibold text-white flex items-center gap-2"><Film className="h-4 w-4 text-amber-400" /> Intro & outro <span className="text-xs font-normal text-gray-500">optional · fades in and out between each part</span></p>
      <div className="flex flex-col sm:flex-row gap-3">
        <BumperSlot kind="intro" items={items} value={intro} onChange={setIntro} ownerId={ownerId} reload={load} defaultId={defaults.intro_id} />
        <BumperSlot kind="outro" items={items} value={outro} onChange={setOutro} ownerId={ownerId} reload={load} defaultId={defaults.outro_id} />
      </div>
      {((intro || null) !== defaults.intro_id || (outro || null) !== defaults.outro_id) && (
        <button data-testid="clip-save-defaults" type="button" onClick={saveDefaults} disabled={savingDef}
          className="text-xs text-amber-400 hover:underline inline-flex items-center gap-1 disabled:opacity-60">
          {savingDef ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Star className="h-3.5 w-3.5" />} Use this intro & outro for every new clip
        </button>
      )}
    </div>
  );
}
